import { useRepositoryMutation, useInvalidateRepositoryQuery } from '@mews-commander/data-access';
import { useEnterpriseNavigate } from '@mews-commander/ui';
import { WithEnterpriseId } from '@mews-commander/types';
import { GDProduct, removeGDService } from '@mews-framework/good-data';
import { invalidateGoodDataAPI } from '@mews-framework/good-data/api';
import { IntegrationCreateFormProps } from '../common';
import { UpdateFormValues, FormValues } from './schema';

const invalidateGoodData = async (invalidateQuery: ReturnType<typeof useInvalidateRepositoryQuery>) => {
    await invalidateQuery('goodData', 'getParameters');
    removeGDService(GDProduct.MewsBI);
    invalidateGoodDataAPI();
};

export const useSubmitHandler = () => {
    const { mutateAsync: createIntegreation, isPending } = useRepositoryMutation('mewsBiIntegration', 'create');
    const navigate = useEnterpriseNavigate();
    const invalidateQuery = useInvalidateRepositoryQuery();

    const handleSubmit: IntegrationCreateFormProps<FormValues>['handleSubmit'] = async (values, _onSuccess) => {
        // Ignore _onSuccess as we don't want to show success alert but instead redirect to MewsBI
        await createIntegreation(values);
        await invalidateGoodData(invalidateQuery);

        void invalidateQuery('mewsBI');
        void invalidateQuery('mewsBiIntegration');

        void navigate({ to: 'BusinessIntelligence' });
    };

    return { handleSubmit, isPending };
};

export const useUpdateHandler = (id: string) => {
    const { mutateAsync: updateAsync, isPending } = useRepositoryMutation('mewsBiIntegration', 'update', {
        transformParams: (values: UpdateFormValues & WithEnterpriseId) => ({
            ...values,
            id,
            isEnabled: values.isEnabled.includes('true'),
        }),
        invalidateQueries: { mewsBiIntegration: ['get'] },
    });
    const invalidateQuery = useInvalidateRepositoryQuery();

    const handleSubmit: IntegrationCreateFormProps<UpdateFormValues>['handleSubmit'] = async (values, onSuccess) => {
        await updateAsync(values);
        await invalidateGoodData(invalidateQuery);
        void invalidateQuery('mewsBI');
        void invalidateQuery('mewsBiIntegration');
        onSuccess();
    };

    return { handleSubmit, isPending };
};

export const useDeleteHandler = (id: string) => {
    const { mutateAsync: deleteAsync, isPending } = useRepositoryMutation('mewsBiIntegration', 'delete');
    const invalidateQuery = useInvalidateRepositoryQuery();

    const handleDelete = async () => {
        await deleteAsync({ id });
        await invalidateGoodData(invalidateQuery);
        void invalidateQuery('mewsBI');
        void invalidateQuery('mewsBiIntegration');
    };

    return { handleDelete, isPending };
};
